// Generates a full Tailwind-style 50-900 shade ramp from a single hex
// color. The admin picks one color (treated as the "700" shade, the
// main interactive color used for buttons/links); everything else is
// derived by mixing toward white (lighter shades) or black (darker
// shades). This is a simple approximation, not true perceptual color
// science -- good enough for a site-wide theme color, not meant for
// precise brand-guideline work.

const hexToRgb = (hex) => {
  const clean = hex.replace('#', '');
  return [
    parseInt(clean.slice(0, 2), 16),
    parseInt(clean.slice(2, 4), 16),
    parseInt(clean.slice(4, 6), 16),
  ];
};

const rgbToHex = ([r, g, b]) =>
  '#' + [r, g, b]
    .map((c) => Math.round(Math.max(0, Math.min(255, c))).toString(16).padStart(2, '0'))
    .join('');

const mixToward = (rgb, target, amount) =>
  rgb.map((c, i) => c + (target[i] - c) * amount);

export const generateColorShades = (baseHex) => {
  if (!baseHex || !/^#[0-9A-Fa-f]{6}$/.test(baseHex)) {
    return null; // caller should fall back to defaults on invalid input
  }
  const base = hexToRgb(baseHex);
  const white = [255, 255, 255];
  const black = [0, 0, 0];

  return {
    50: rgbToHex(mixToward(base, white, 0.93)),
    100: rgbToHex(mixToward(base, white, 0.82)),
    200: rgbToHex(mixToward(base, white, 0.68)),
    300: rgbToHex(mixToward(base, white, 0.52)),
    400: rgbToHex(mixToward(base, white, 0.32)),
    500: rgbToHex(mixToward(base, white, 0.15)),
    600: rgbToHex(mixToward(base, white, 0.03)),
    700: baseHex,
    800: rgbToHex(mixToward(base, black, 0.18)),
    900: rgbToHex(mixToward(base, black, 0.35)),
  };
};

// Each option is a real heading/body pairing, not a single font
// applied everywhere -- a deliberate display face for headings paired
// with a distinct, highly-readable body face is what actually gives
// a site typographic personality (see FONT_STACK_LABELS below for
// what each is meant to feel like).
export const FONT_STACKS = {
  newsreader: {
    heading: `'Newsreader', Georgia, serif`,
    body: `'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
  },
  system: {
    heading: `-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif`,
    body: `-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif`,
  },
  serif: {
    heading: `Georgia, 'Times New Roman', Times, serif`,
    body: `Georgia, 'Times New Roman', Times, serif`,
  },
  mono: {
    heading: `'Courier New', Courier, monospace`,
    body: `'Courier New', Courier, monospace`,
  },
  rounded: {
    heading: `ui-rounded, 'SF Pro Rounded', 'Segoe UI', sans-serif`,
    body: `ui-rounded, 'SF Pro Rounded', 'Segoe UI', sans-serif`,
  },
};

export const FONT_STACK_LABELS = {
  newsreader: 'Newsreader + IBM Plex Sans (recommended -- built for long-form reading)',
  system: 'System default (fastest, no download)',
  serif: 'Classic Serif (Georgia)',
  mono: 'Monospace (Courier New)',
  rounded: 'Rounded Sans (system rounded fonts where available)',
};